
import { useEffect, useRef } from "react";
import { isHandlingScroll } from "@/utils/scrollManager";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * A hook that scrolls to a reference element when a dependency changes
 * with enhanced mobile support
 * @param dependency The value to watch for changes
 * @param options Configuration options
 */
export const useScrollToNewContent = <T>(
  dependency: T,
  options: {
    behavior?: ScrollBehavior;
    delay?: number;
    condition?: boolean;
    offset?: number;
  } = {}
) => {
  const { 
    behavior = "smooth", 
    delay = 100, 
    condition = true,
    offset = 0 
  } = options;
  const ref = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!dependency || !condition || isHandlingScroll()) return;

    const timer = setTimeout(() => {
      if (ref.current) {
        const elementPosition = ref.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        // Use different behavior on mobile for better performance
        const scrollBehavior = isMobile ? 'auto' : behavior;

        window.scrollTo({
          top: offsetPosition,
          behavior: scrollBehavior,
        });
      }
    }, isMobile ? delay / 2 : delay); // Faster response on mobile

    return () => clearTimeout(timer);
  }, [dependency, behavior, delay, condition, offset, isMobile]);

  return ref;
};
